import { ProductType } from './ProductType';
import { PageType } from './PageType';
import { LayoutType } from './LayoutTypes';
import { placeholderTypes } from './placeholderTypes';
import {TemplateType} from "./TemplateType";
import { AgronomyChartPosition } from './enums';
import { CoverDocumentType } from "./CoverImageTypes";

export interface ICrop {
  id: number;
  name: string;
  defaultImage: string;
  images: string[];
}
export interface ILogo {
  title: string;
  image: string;
  id?: number;
  archived: boolean;
}

export interface IBranding {
  title: string;
  image: string;
  coverImage: string;
  id?: number;
}
export interface ICoverImage {
  title: string;
  coverType: ProductLine;
  coverDocumentType: CoverDocumentType;
  web: {
    fullCover: string;
    frontCover: string;
    backCover: string;
  };
  print: {
    fullCover: string;
  };
  id?: number;
}

export interface ITagline {
  id: number,
  title: string;
  productLine: ProductLine;
}

export interface IState {
  id: number;
  state_name: string;
  state_abbreviation: string;
  country: string;
}

export interface IShippingInfo {
  shipName: string;
  shipAddress1: string;
  shipAddress2: string;
  shipCity: string;
  shipState: string;
  shipPostalCode: string;
  shipCountry: string;
  divisionId: string;
}

export interface IPdfAnnotation {
  page: number;
  position: number[]; // x1, y1, width, height
  content: string;
}

export interface IDocument {
  id: number;
  type: ProductType;
  name: string;
  spreads: ISpread[];
  pages: IPage[];
  crops: ICrop[];
  postcardImageFront: IPostcardImage | null;
  postcardImageBack: IPostcardImage | null;
  posterImageBackground: IPosterImage | null;
  branding: IBranding | null;
  brandings: IBranding[];
  logos: ILogo[];
  coverImage: ICoverImage | null;
  companyInfo: {
    isEnabled: boolean;
    divisionName: string;
    phoneNumber: string;
    email: string;
  };
  additionalInfo: {
    text: string,
    isEnabled: boolean,
    date: string;
  };
  title: string;
}

export interface IEditorState {
  selectedPage: IPage | null;
  selectedSpread: ISpread | null;
  isSaved: boolean;
  zoomLevel: number;
}

export interface MainViewProps {
}

export interface ProductCategory {
  id: number;
  name: string;
  products: IBrochureProduct[];
}

export interface IBrochureProduct {
  type: any;
  id: number;
  name: string;
  category: string;
  category_2: string | undefined;
  group: string;
  published: boolean;
  statement: string;
  features: string;
  rates: string;
  country: string;
  icon: any;
  logo: string;
  img: string;
  disclaimers: any[];
  templateType?: string;
  variableText: any;
  imageArea?: string;
}

export interface IDisclaimer {
  id?: number;
  document_type: string;
  value: string;
  description: string;
}

export enum ProductLine {
    LOVELAND,
    SEED,
    ALL
}

export interface IOrderQuantities_Addresses {
  orderQuantity: number;
  address: IShippingInfo;
}

export interface IOrder {
  id: number;
  document: IDocument;
  created_at: string;
  last_modified: string;
  status_id: number;
  user?: IUserData;
  cost?: number;
  comments: string;
  shipEmail: string;
  orderQuantitiesAddresses: IOrderQuantities_Addresses[];

}
export interface IOrderPlacedReportRow {
  orderId: number;
    creationDate: Date;
    approvalDate?: Date;
    orderStatus: String;
    userEmail: String;
    userName: String;
    shippingAddress: String;
    orderQuantity: Number;
    divisionId: String;
    documentId: Number;
}
export interface IOrderCostReportRow {
  divisionId: string;
  orderCount: number;
  totalCost: number;
}

export interface IOrderStatus {
  id: number;
  status_name: string;
  status_admin_name: string;
  status_user_name: string;
  user_status: number;
  admin_status: number;
}

export interface ISpread {
  left: IPage;
  right: IPage | null;
  id?: number;
}

export interface IPage {
  title: string;
  type: PageType;
  number: number;
  position: string;
  placeholders: IPlaceHolder[];
  layout?: LayoutType;
}

export interface IPlaceHolder {
  data: IBrochureProduct | IProduct | IImage | null;
  accepts: string[];
  type: number;
}

export interface IProduct {
    type: ProductType;
    id: number;
    published: boolean;
    name: string;
    category: string;
    category_2?: string;
    group: string;
    statement: string;
    features: string;
    grower_benefits?: string;
    rates: string;
    country: string;
    active_ingredients?: string;
    analysis?: string;
    target_chemistries?: string;
    icon: any;
    logo: string;
    disclaimers: any[];
    templateType?: string;
    variableText: any;            //Represents either active ingredient, target chem, or analysis.
    imageArea?: string;
    recomendation_1?: string;
    recomendation_2?: string;
    recomendation_3?: string;
    separator_1?: string;         //Separators are the "or" or "and" words
    separator_2?: string;
    separator_3?: string;
    url?:string;
    comment?: string;
    productURL?: string;
}

export interface IImage {
  src: string;
  type: placeholderTypes;
  category: string;
  disclaimers?: any[];
  imageArea?:string;
}

export interface IUserData {
  id: string;
  role: string;
  email: string;
  username: string;
}

export interface IValidationItem {
  cssSelector: string;
  status: boolean;
  message: string;
}

export interface IDivider {
  name: string;
  bg: string;
  logo: string;
  line: string;
  type: placeholderTypes;
}

export interface IBaseDivider {
  id: number;
  image_path: string;
}

export interface ISeedDivider extends IBaseDivider{
  category: ISeedCategory;
}

export interface IShelfDivider extends IBaseDivider{
  category: IShelfCategory;
}

export interface ICreateProductState {
    templateType: TemplateType;
    documentType: ProductType;
}

export enum ITechReferenceSheetType {
  AGRONOMY_SHEET = "Agronomy",
  SUSTAINABILITY_SHEET = "Sustainability"
}

export interface ITechReferenceSheet {
  id: number;
  src: string;
  title: string;
  published: boolean;
  sheet_category: ITechReferenceSheetType;
}

export interface IBlank {
  id: number;
  src: string;
  type: placeholderTypes;
  published: boolean;
  title: string;
  url: string;
}

export interface ICropDivider {
  name: string;
  img: string;
  bg_type: string;
}

export interface ITechSheet {
  id: number;
  image_path: string;
  name: string;
  type: placeholderTypes;
  published: boolean;
  category: ISeedCategory;
}

export enum DownloadDocumentType {
  PDF = 1,
  CSV = 2,
  ZIP = 3,
 }
export interface IUserDownloadList {
  id: number;
  name: string;
  status: number;
  documentType: DownloadDocumentType;
}

export interface IDragInfo {
  type: string;
  dragIndex: number;
  dropIndex: number;
}

export interface IFillerImage {
  id: number;
  name: string;
  src: string;
}

export interface IPostcardImage {
  id: number;
  name: string;
  image_src: string;
  side: string;
  archived: boolean;
  published: boolean;
}

export interface IPosterImage {
  id: number;
  name: string;
  image_src: string;
  web_image_path: string;
  is_archived: string;
}

export interface IProductLogo {
  id: string;
  src: string;
  archived: boolean;
}

export interface IProductIcon {
  id: string;
  src: string;
}

export interface ISeedCategory {
  id: number;
  name: string;
  parentName: string;
  attributes: ISeedAttribute[];
}

export interface IShelfCategory {
  id: number;
  name: string;
}

export interface ISeedAttribute {
  id: string;
  name: string;
  createSeedDisplayName: string;
  productDescriptionDisplayName: string;
  agronomyChartDisplayName: string;
  agronomyChartIndex: number;
  agronomyChartPosition: AgronomyChartPosition;
  grouping: string;
}

export interface ISeedAttributeValue {
  id: string | undefined; //unique id for this value
  value: string;
  attribute: ISeedAttribute;
}

export interface ISeedIcon {
  id: number;
  name: string;
  src: string;
  category: ISeedCategory;
  published: boolean;
}

export interface IEmptySeed {
  id: number | undefined;
  name: string;
  published: boolean;
  isNew: boolean;
  brand: boolean;
  category: ISeedCategory | undefined;
  bulletPointMarkdown: string;
  attributes: ISeedAttributeValue[];
  icons: ISeedIcon[];
  type: string;
}

export interface ISeedProduct {
  id: number;
  name: string;
  type: string;
  isNew: boolean;
  brand: boolean;
  published: boolean;
  category: ISeedCategory;
  bulletPointMarkdown: string;
  attributes: ISeedAttributeValue[];
  icons: ISeedIcon[];
}

export interface ISeedProductSubmit {
  id: number | undefined;
  name: string;
  type: string;
  isNew: boolean;
  brand: boolean;
  published: boolean;
  categoryId: string|number;
  bulletPointMarkdown: string;
  attributes: {attributeId: string, value: string}[];
  icons: ISeedIcon[];
}
export interface ISeedProductClone {
  bulletPointMarkdown: string;
  seedToCloneId: number;
}

export interface ICreatePdfRequest {
  name: string;
  content: string;
  type: ProductType;
  documentId: number;
  socketID: string;
  isPrintDownload: boolean;
  userID: string,
  userEmail: string;
  annotations: IPdfAnnotation[];
}



// WEBPACK FOOTER //
// ./src/infrastructure/interfaces.ts